/**
 * Turn an API error into something a person can act on.
 *
 * The backend already sends a safe `message`; this adds the "so what do I do"
 * half, which depends on the error code and is the same for every connector.
 */

import { ApiRequestError } from '@/core/api/base.api.client'
import type { ApiError } from '@/core/types/api.types'

export interface FormattedError {
  title: string
  message: string
  hint?: string
  fieldErrors: Record<string, string>
  retryable: boolean
  code: string
  requestId?: string | null
  /** Structured extras from the backend, e.g. the provider's raw response. */
  details?: ApiError['details']
}

const HINTS: Record<string, string> = {
  invalid_credentials:
    'The stored credentials were rejected. Reconnect this app to replace them.',
  token_expired: 'Reconnect this app to get a fresh token.',
  connection_revoked: 'Reconnect this app to continue.',
  insufficient_scope:
    'Reconnect this app and approve the additional permissions it asks for.',
  rate_limited: 'The provider is throttling this connection. Wait a moment and retry.',
  validation_failed: 'Correct the highlighted fields and run it again.',
  config_error:
    'This is a server configuration problem, not something you can fix from here.',
  ssrf_blocked: 'That destination is not permitted.',
  network_error: 'Start the backend with: uvicorn app.main:app --reload --port 8000',
  connector_not_found: 'The connector may have been removed from the server.',
  action_not_found: 'The action may have been renamed or removed.',
}

const TITLES: Record<string, string> = {
  invalid_credentials: 'Credentials rejected',
  token_expired: 'Token expired',
  connection_revoked: 'Connection revoked',
  insufficient_scope: 'Missing permissions',
  rate_limited: 'Rate limited',
  validation_failed: 'Check your input',
  upstream_error: 'The connected app returned an error',
  network_error: 'Cannot reach the API',
  config_error: 'Server misconfigured',
}

export function formatError(error: unknown): FormattedError {
  if (error instanceof ApiRequestError) {
    const missing = error.details?.missing_scopes
    const hint =
      missing && missing.length > 0
        ? `Missing scopes: ${missing.join(', ')}. Reconnect and approve them.`
        : HINTS[error.code]

    return {
      title: TITLES[error.code] ?? 'Something went wrong',
      message: error.message,
      hint,
      fieldErrors: error.fieldErrors,
      retryable: error.retryable,
      code: error.code,
      requestId: error.requestId,
      details: error.details,
    }
  }

  if (error instanceof Error) {
    return {
      title: 'Something went wrong',
      message: error.message,
      fieldErrors: {},
      retryable: false,
      code: 'unknown',
    }
  }

  return {
    title: 'Something went wrong',
    message: String(error),
    fieldErrors: {},
    retryable: false,
    code: 'unknown',
  }
}
